import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  forwardRef,
  MouseEvent,
  PropsWithChildren,
} from "react";
import {
  DisplayStatus,
  DisplaySize,
  Alignment,
  Direction,
} from "../../utils/types/component-configs";
import { pickStylesForRowAlignBasedOnParams } from "../../utils/helpers/row-column-align";
import { RowAlignBaseProps } from "../row-align/RowAlign";
import { Link } from "gatsby";
import styles from "./Button.module.scss";
import { createClasses } from "../../utils/helpers/browser-helper";
import SVGIcon, { SVGIconProps } from "../svg-icon/SvgIcon";

type ButtonBaseRowProps = {
  alignDirection: Alignment.HORIZONTAL;
  rowProps?: RowAlignBaseProps;
  iconConfig?: Omit<SVGIconProps, "children"> & {
    renderIcon: SVGIconProps["children"];
    iconDirection?: Direction;
  };
};

type ButtonBaseNoRowProps = {
  alignDirection?: never;
  rowProps?: never;
  iconConfig?: never;
};

export type ButtonBaseProps = {
  classes?: string;
  contentClasses?: string;
  buttonRole?: string;
  buttonOutline?: boolean;
  fullWidth?: boolean;
  sizes?: string;
  loader?: boolean;
} & (ButtonBaseRowProps | ButtonBaseNoRowProps);

export const enum ButtonType {
  BUTTON = "BUTTON",
  LINK = "LINK",
}

export type ButtonElementProps = {
  elementType?: ButtonType.BUTTON;
  buttonClick: (event: MouseEvent<HTMLButtonElement>) => void;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "className">;

export type LinkElementProps = {
  elementType: ButtonType.LINK;
  href: string;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className">;

export type ButtonComponentProps = PropsWithChildren<ButtonBaseProps>;

export type ButtonProps = ButtonComponentProps &
  (ButtonElementProps | LinkElementProps);

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  function createClassesForButtons(partialProps: ButtonBaseProps) {
    const classNames = [];

    switch (partialProps.buttonRole) {
      case DisplayStatus.PRIMARY:
        classNames.push(styles.isPrimary);
        break;
      case DisplayStatus.INFO:
        classNames.push(styles.isInfo);
        break;
      case DisplayStatus.ERROR:
        classNames.push(styles.isDanger);
        break;
      default:
        break;
    }

    switch (partialProps.sizes) {
      case DisplaySize.SMALL:
        classNames.push(styles.isSmall);
        break;
      case DisplaySize.MEDIUM:
        classNames.push(styles.isMedium);
        break;
      case DisplaySize.LARGE:
        classNames.push(styles.isLarge);
        break;
      default:
        break;
    }

    if (partialProps.buttonOutline) {
      classNames.push(styles.isOutlined);
    }

    if (partialProps.fullWidth) {
      classNames.push(styles.isFullwidth);
    }

    if (partialProps.loader) {
      classNames.push(styles.isLoading, styles.overlayLoader);
    }

    // Either some button properties are being used or the button must behave as the default style
    // Add the basic button style
    if (
      classNames.length > 0 ||
      partialProps.buttonRole === DisplayStatus.DEFAULT
    ) {
      classNames.push(styles.button);
    }

    if (partialProps.alignDirection === Alignment.HORIZONTAL) {
      classNames.push(
        pickStylesForRowAlignBasedOnParams(partialProps.rowProps ?? {}, styles)
      );
    }

    if (partialProps.classes) {
      classNames.push(partialProps.classes);
    }

    return classNames;
  }

  function renderIcon(iconConfig: ButtonBaseRowProps["iconConfig"]) {
    if (!iconConfig) {
      return null;
    }

    const { renderIcon, iconProps, ...restIconConfig } = iconConfig;
    const iconClasses = [styles.contentIcon, iconProps?.className];
    return (
      <SVGIcon
        {...restIconConfig}
        iconProps={{ ...iconProps, className: createClasses(iconClasses) }}
      >
        {renderIcon}
      </SVGIcon>
    );
  }

  function renderContent({
    children,
    contentClasses,
  }: Pick<ButtonProps, "children" | "contentClasses">) {
    if (!children) {
      return null;
    }

    return <span className={createClasses([contentClasses])}>{children}</span>;
  }

  function renderChildren({
    children,
    contentClasses,
    iconConfig,
  }: Pick<ButtonProps, "children" | "contentClasses" | "iconConfig">) {
    return (
      <>
        {iconConfig?.iconDirection === Direction.LEFT && renderIcon(iconConfig)}
        {renderContent({ contentClasses, children })}
        {/* Render by default on the right */}
        {(iconConfig?.iconDirection === Direction.RIGHT ||
          !iconConfig?.iconDirection) &&
          renderIcon(iconConfig)}
      </>
    );
  }

  if (props.elementType === ButtonType.LINK) {
    const {
      href,
      children,
      classes,
      sizes,
      loader = false,
      buttonRole,
      buttonOutline = false,
      fullWidth = false,
      alignDirection,
      rowProps,
      iconConfig,
      elementType,
      contentClasses,
      ...restLinkProps
    } = props;

    let buttonBaseProps: ButtonBaseProps = {
      classes,
      sizes,
      loader,
      buttonRole,
      buttonOutline,
      fullWidth,
      alignDirection,
    };

    if (buttonBaseProps.alignDirection === Alignment.HORIZONTAL) {
      buttonBaseProps.rowProps = rowProps;
    }

    const className = createClassesForButtons(buttonBaseProps);

    return (
      <Link to={href} className={createClasses(className)} {...restLinkProps}>
        {renderChildren({ contentClasses, children, iconConfig })}
      </Link>
    );
  }

  const onButtonClick = function (event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (buttonClick) {
      buttonClick(event);
    }
  };

  const {
    buttonClick,
    disabled,
    children,
    classes,
    sizes,
    loader = false,
    buttonRole,
    buttonOutline = false,
    fullWidth = false,
    alignDirection,
    rowProps,
    elementType,
    iconConfig,
    contentClasses,
    ...restbutttonProps
  } = props;

  let buttonBaseProps: ButtonBaseProps = {
    classes,
    sizes,
    loader,
    buttonRole,
    buttonOutline,
    fullWidth,
    alignDirection,
  };

  if (buttonBaseProps.alignDirection === Alignment.HORIZONTAL) {
    buttonBaseProps.rowProps = rowProps;
  }

  const className = createClassesForButtons(buttonBaseProps);

  return (
    <button
      disabled={loader || disabled}
      {...restbutttonProps}
      onClick={onButtonClick}
      className={createClasses(className)}
      ref={ref}
    >
      {renderChildren({ contentClasses, children, iconConfig })}
    </button>
  );
});

export default Button;

type ButtonsProps = {
  align?: string;
  classes?: string;
  hasAddons?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  removeBottomSpacing?: boolean;
};

export function Buttons({
  children,
  align,
  hasAddons,
  classes,
  onClick,
  removeBottomSpacing,
}: React.PropsWithChildren<ButtonsProps>) {
  let buttonsClasses = `${styles.buttons} ${styles.buttonsContainer}`;
  switch (align) {
    case Alignment.RIGHT:
      buttonsClasses += ` ${styles.isRight}`;
      break;
    case Alignment.CENTER:
      buttonsClasses += ` ${styles.isCentered}`;
      break;
    default:
      break;
  }

  if (hasAddons) {
    buttonsClasses += ` ${styles.hasAddons}`;
  }

  if (removeBottomSpacing) {
    buttonsClasses += ` ${styles.removeBottomSpacing}`;
  }

  if (classes) {
    buttonsClasses += ` ${classes}`;
  }

  return (
    <div className={buttonsClasses} onClick={onClick}>
      {children}
    </div>
  );
}
